import { getMemberAccessToken, supabase } from '../auth/supabase';

export const PROGRAM_FEED_BUCKET = 'program-feed';
export const MAX_PROGRAM_FEED_ATTACHMENTS = 5;
export const MAX_PROGRAM_FEED_ATTACHMENT_BYTES = 15 * 1024 * 1024;
export const PROGRAM_FEED_ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/heic,image/heif,application/pdf,.pdf,.jpg,.jpeg,.png,.heic,.heif';

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf']);

function safeFileName(value) {
  return String(value || 'attachment')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(-120) || 'attachment';
}

function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function attachmentSizeLabel(bytes) {
  const size = Number(bytes || 0);
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
}

export function validateProgramFeedFile(file) {
  if (!file) return 'Choose a file.';
  if (!ALLOWED_MIME.has(String(file.type || '').toLowerCase())) {
    return 'Only PDF, JPEG, PNG, HEIC, or HEIF files are supported.';
  }
  if (!Number(file.size || 0) || Number(file.size) > MAX_PROGRAM_FEED_ATTACHMENT_BYTES) {
    return 'Each attachment must be 15 MB or smaller.';
  }
  return '';
}

export async function uploadProgramFeedAttachment(file, post) {
  const validation = validateProgramFeedFile(file);
  if (validation) throw new Error(validation);
  if (!post?.id || !post?.revision) throw new Error('Save the Program Feed post before uploading attachments.');

  const storagePath = `${post.id}/${uuid()}-${safeFileName(file.name)}`;
  const { error: uploadError } = await supabase.storage
    .from(PROGRAM_FEED_BUCKET)
    .upload(storagePath, file, {
      cacheControl: '3600',
      contentType: file.type,
      upsert: false,
    });
  if (uploadError) throw uploadError;

  try {
    const token = await getMemberAccessToken();
    if (!token) throw new Error('Member sign in required.');
    const response = await fetch('/api/program-feed-attachment', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'finalize',
        postId: post.id,
        revision: post.revision,
        storagePath,
        originalName: file.name,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || 'Unable to validate the attachment.');
    return payload.attachment;
  } catch (error) {
    await supabase.storage.from(PROGRAM_FEED_BUCKET).remove([storagePath]).catch(() => {});
    throw error;
  }
}

export async function loadActiveProgramFeedAttachments(postId) {
  if (!postId) return [];
  const { data, error } = await supabase
    .from('program_feed_attachments')
    .select('*')
    .eq('post_id', postId)
    .is('removed_revision', null)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function signProgramFeedAttachments(rows, expiresIn = 900) {
  const list = Array.isArray(rows) ? rows : [];
  const signed = await Promise.all(list.map(async row => {
    const { data, error } = await supabase.storage
      .from(PROGRAM_FEED_BUCKET)
      .createSignedUrl(row.storage_path, expiresIn);
    return {
      ...row,
      signedUrl: error ? '' : String(data?.signedUrl || ''),
      signedError: error?.message || '',
    };
  }));
  return signed;
}
